// Purchase Optimizer: upload 2-5 supplier quotations for the same materials,
// review what the AI extracted and how it matched the materials across quotes,
// then get the cheapest supplier per material and raise one PO per supplier.
//
// The backend (/purchase-optimizer/*) is stateless - this component holds the
// whole comparison and sends it with every call. The optimiser only picks the
// recommendation; the totals for the user's own edits are summed here.
import React, { useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Paper, Select, Step, StepLabel, Stepper, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const API = 'https://cd9d-205-254-163-120.ngrok-free.app/purchase-optimizer';
const MIN_FILES = 2;
const MAX_FILES = 5;
const STEPS = ['Upload quotations', 'Review extracted prices', 'Recommendation & POs'];

const fmt = (value, digits = 2) =>
  value === null || value === undefined || Number.isNaN(value)
    ? '—'
    : Number(value).toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const priceKey = (materialId, quoteId) => `${materialId}|${quoteId}`;
const num = (value) => (value === '' || value === null || value === undefined ? null : Number(value));

async function readError(response, fallback) {
  try {
    const body = await response.json();
    if (typeof body.detail === 'string') return body.detail;
    if (Array.isArray(body.detail)) return body.detail.map((d) => d.msg).join(', ');
  } catch {
    // not JSON
  }
  return fallback;
}

const PurchaseOptimizer = () => {
  const [step, setStep] = useState(0);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Extraction result + everything the user can edit.
  const [quotes, setQuotes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [prices, setPrices] = useState({});
  const [warnings, setWarnings] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState('INR');
  const [rates, setRates] = useState({});

  // Optimisation result and the user's overrides on top of it.
  const [result, setResult] = useState(null);
  const [assignment, setAssignment] = useState({});
  const [stale, setStale] = useState(false);

  // PO step.
  const [supplierCodes, setSupplierCodes] = useState({});
  const [sapMaterial, setSapMaterial] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [poResults, setPoResults] = useState({});

  const quoteById = useMemo(() => Object.fromEntries(quotes.map((q) => [q.id, q])), [quotes]);
  const lineOf = (quoteId, lineNo) => quoteById[quoteId]?.items.find((i) => i.line_no === lineNo);
  const currencies = useMemo(() => [...new Set(quotes.map((q) => q.currency).filter(Boolean))], [quotes]);
  const rateOf = (currency) => (currency === baseCurrency ? 1 : num(rates[currency]));

  const touched = () => { if (result) setStale(true); };

  // ---------------------------------------------------------------- upload
  const addFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    const pdfs = picked.filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    const next = [...files];
    pdfs.forEach((f) => {
      if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
    });
    setError(pdfs.length < picked.length ? 'Only PDF files can be added.' : next.length > MAX_FILES ? `You can compare at most ${MAX_FILES} quotations.` : '');
    setFiles(next.slice(0, MAX_FILES));
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const startOver = () => {
    setStep(0); setFiles([]); setQuotes([]); setMaterials([]); setPrices({}); setWarnings([]);
    setRates({}); setResult(null); setAssignment({}); setStale(false); setSupplierCodes({});
    setSapMaterial({}); setPoResults({}); setError('');
  };

  const extract = async () => {
    setBusy('extract'); setError('');
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    try {
      const response = await fetch(`${API}/extract`, { method: 'POST', body: form });
      if (!response.ok) throw new Error(await readError(response, 'Could not read the quotations.'));
      const data = await response.json();
      const initialPrices = {};
      data.materials.forEach((m) => {
        Object.entries(m.offers).forEach(([quoteId, lineNo]) => {
          const line = data.quotes.find((q) => q.id === quoteId)?.items.find((i) => i.line_no === lineNo);
          initialPrices[priceKey(m.id, quoteId)] = line ? line.unit_price : null;
        });
      });
      setQuotes(data.quotes);
      setMaterials(data.materials);
      setPrices(initialPrices);
      setWarnings(data.warnings || []);
      setBaseCurrency(data.base_currency || 'INR');
      setRates({});
      setSupplierCodes(Object.fromEntries(data.quotes.map((q) => [q.id, q.supplier_code || ''])));
      setSapMaterial(Object.fromEntries(data.materials.map((m) => [m.id, /^\d+$/.test(m.material_code || '') ? m.material_code : ''])));
      setResult(null); setAssignment({}); setPoResults({});
      setStep(1);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  // ---------------------------------------------------------------- review edits
  const updateMaterial = (id, patch) => {
    setMaterials((ms) => ms.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    touched();
  };

  const removeMaterial = (id) => {
    setMaterials((ms) => ms.filter((m) => m.id !== id));
    setAssignment((a) => {
      const next = { ...a };
      delete next[id];
      return next;
    });
    touched();
  };

  // Point a material at a different line of one quote (or none). A line can only
  // belong to one material, so it is taken away from whichever had it.
  const setOffer = (materialId, quoteId, lineNo) => {
    setMaterials((ms) => ms.map((m) => {
      const offers = { ...m.offers };
      if (m.id === materialId) {
        if (lineNo === '') delete offers[quoteId]; else offers[quoteId] = lineNo;
      } else if (lineNo !== '' && offers[quoteId] === lineNo) {
        delete offers[quoteId];
      }
      return { ...m, offers };
    }));
    setPrices((p) => ({
      ...p,
      [priceKey(materialId, quoteId)]: lineNo === '' ? null : lineOf(quoteId, lineNo)?.unit_price ?? null,
    }));
    touched();
  };

  const setPrice = (materialId, quoteId, value) => {
    setPrices((p) => ({ ...p, [priceKey(materialId, quoteId)]: value }));
    touched();
  };

  const unmatchedLines = useMemo(() => {
    const used = new Set(materials.flatMap((m) => Object.entries(m.offers).map(([q, l]) => `${q}|${l}`)));
    return quotes.flatMap((q) => q.items.filter((i) => !used.has(`${q.id}|${i.line_no}`)).map((i) => ({ quote: q, line: i })));
  }, [materials, quotes]);

  const addMaterialFromLine = (quote, line) => {
    const id = `M${Date.now()}`;
    setMaterials((ms) => [...ms, {
      id, name: line.description, material_code: line.material_code, unit: line.unit,
      required_qty: line.quantity || 0, offers: { [quote.id]: line.line_no },
    }]);
    setPrices((p) => ({ ...p, [priceKey(id, quote.id)]: line.unit_price }));
    setSapMaterial((s) => ({ ...s, [id]: /^\d+$/.test(line.material_code || '') ? line.material_code : '' }));
    touched();
  };

  const priceOf = (materialId, quoteId) => {
    const value = num(prices[priceKey(materialId, quoteId)]);
    return value && value > 0 ? value : null;
  };

  // ---------------------------------------------------------------- optimise
  const optimise = async () => {
    setBusy('optimise'); setError('');
    try {
      const response = await fetch(`${API}/optimize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          base_currency: baseCurrency,
          exchange_rates: Object.fromEntries(currencies.filter((c) => c !== baseCurrency).map((c) => [c, num(rates[c])])),
          quotes: quotes.map((q) => ({ id: q.id, supplier_name: q.supplier_name, currency: q.currency })),
          materials: materials.map((m) => ({
            id: m.id,
            name: m.name,
            required_qty: num(m.required_qty) || 0,
            prices: Object.fromEntries(Object.keys(m.offers).map((q) => [q, priceOf(m.id, q)])),
          })),
        }),
      });
      if (!response.ok) throw new Error(await readError(response, 'Could not compute the best combination.'));
      const data = await response.json();
      setResult(data);
      setAssignment(Object.fromEntries(data.allocation.map((a) => [a.material_id, a.quote_id])));
      setStale(false);
      setPoResults({});
      setStep(2);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const recommended = useMemo(
    () => Object.fromEntries((result?.allocation || []).map((a) => [a.material_id, a])),
    [result],
  );

  // Lines of the current selection, recomputed from the user's edits.
  const selection = useMemo(() => materials
    .filter((m) => assignment[m.id])
    .map((m) => {
      const quoteId = assignment[m.id];
      const quote = quoteById[quoteId];
      const unitPrice = priceOf(m.id, quoteId);
      const rate = rateOf(quote?.currency);
      const qty = num(m.required_qty) || 0;
      const line = lineOf(quoteId, m.offers[quoteId]);
      return {
        material: m, quoteId, quote, line, qty, unitPrice,
        unitPriceBase: unitPrice !== null && rate ? unitPrice * rate : null,
        lineTotalBase: unitPrice !== null && rate ? unitPrice * rate * qty : null,
      };
    }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [materials, assignment, prices, rates, baseCurrency, quoteById]);

  const selectionTotal = selection.reduce((sum, s) => sum + (s.lineTotalBase || 0), 0);
  const orders = useMemo(() => {
    const byQuote = {};
    selection.forEach((s) => { (byQuote[s.quoteId] = byQuote[s.quoteId] || []).push(s); });
    return quotes.filter((q) => byQuote[q.id]).map((q) => ({ quote: q, lines: byQuote[q.id] }));
  }, [selection, quotes]);

  const resetToRecommendation = () => {
    setAssignment(Object.fromEntries((result?.allocation || []).map((a) => [a.material_id, a.quote_id])));
  };

  // ---------------------------------------------------------------- create POs
  const pendingOrders = orders.filter((o) => !poResults[o.quote.id]?.success);

  const createPOs = async () => {
    setConfirmOpen(false); setBusy('po'); setError('');
    try {
      const response = await fetch(`${API}/create-pos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orders: pendingOrders.map(({ quote, lines }) => ({
            quote_id: quote.id,
            supplier_name: quote.supplier_name,
            supplier_code: supplierCodes[quote.id] || null,
            supplier_gst: quote.supplier_gst || null,
            currency: quote.currency,
            quotation_date: quote.quotation_date,
            delivery_date: quote.delivery_date,
            payment_terms: quote.payment_terms,
            items: lines.map((s) => ({
              material_code: sapMaterial[s.material.id] || null,
              description: s.material.name,
              quantity: s.qty,
              unit: s.line?.unit || s.material.unit,
              unit_price: s.unitPrice,
              tax_code: s.line?.tax_code || null,
            })),
          })),
        }),
      });
      if (!response.ok) throw new Error(await readError(response, 'PO creation failed.'));
      const data = await response.json();
      setPoResults((prev) => ({ ...prev, ...Object.fromEntries(data.results.map((r) => [r.quote_id, r])) }));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const anyPoCreated = Object.values(poResults).some((r) => r.success);
  const missingRates = currencies.filter((c) => c !== baseCurrency && !(num(rates[c]) > 0));
  const badQty = materials.some((m) => !(num(m.required_qty) > 0));
  const canOrder = selection.length > 0 && !stale && selection.every((s) => s.unitPrice !== null && s.qty > 0);

  // ================================================================ render
  return (
    <Box sx={{ flexGrow: 1, overflowY: 'auto', p: { xs: 1.5, md: 3 }, backgroundColor: 'var(--bg)', color: 'var(--text)' }}>
      <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Purchase Optimizer</Typography>
            <Typography variant="body2" color="text.secondary">
              Compare {MIN_FILES}–{MAX_FILES} quotations for the same materials and order each material from the cheapest supplier.
            </Typography>
          </Box>
          {step > 0 && (
            <Button startIcon={<RestartAltIcon />} onClick={startOver} disabled={!!busy}>New comparison</Button>
          )}
        </Box>

        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((label, i) => (
            <Step key={label} completed={step > i}>
              <StepLabel
                onClick={() => { if (i < step && !busy && !anyPoCreated) setStep(i); }}
                sx={{ cursor: i < step && !anyPoCreated ? 'pointer' : 'default' }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* ------------------------------------------------ step 0: upload */}
        {step === 0 && (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Button component="label" variant="contained" startIcon={<UploadFileIcon />} disabled={files.length >= MAX_FILES || !!busy}>
              Add quotation PDFs
              <input type="file" hidden multiple accept="application/pdf" onChange={addFiles} ref={fileInputRef} />
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {files.length} of {MAX_FILES} added · at least {MIN_FILES} needed
            </Typography>

            {files.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 2 }}>
                {files.map((f, i) => (
                  <Chip
                    key={`${f.name}-${f.size}`}
                    label={`${i + 1}. ${f.name}`}
                    onDelete={busy ? undefined : () => setFiles(files.filter((x) => x !== f))}
                  />
                ))}
              </Box>
            )}

            <Box sx={{ mt: 3 }}>
              <Button variant="contained" color="primary" onClick={extract} disabled={files.length < MIN_FILES || !!busy} sx={{ minWidth: 220 }}>
                {busy === 'extract' ? <><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />Reading quotations…</> : 'Extract & compare'}
              </Button>
              {busy === 'extract' && (
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                  The AI reads each quotation, then matches the materials across them. This takes about 30–60 seconds.
                </Typography>
              )}
            </Box>
          </Paper>
        )}

        {/* ------------------------------------------------ step 1: review */}
        {step === 1 && (
          <>
            {warnings.map((w) => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}

            <QuoteSummary quotes={quotes} />

            {currencies.length > 1 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Currency conversion (prices compared before tax)</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Compare in</Typography>
                    <Select size="small" value={baseCurrency} onChange={(e) => { setBaseCurrency(e.target.value); touched(); }}>
                      {currencies.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                  </Box>
                  {currencies.filter((c) => c !== baseCurrency).map((c) => (
                    <TextField
                      key={c} size="small" type="number" label={`1 ${c} = ? ${baseCurrency}`}
                      value={rates[c] ?? ''} error={!(num(rates[c]) > 0)}
                      onChange={(e) => { setRates((r) => ({ ...r, [c]: e.target.value })); touched(); }}
                      sx={{ width: 170 }} inputProps={{ min: 0, step: 'any' }}
                    />
                  ))}
                </Box>
              </Paper>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Each row is one material. Check that the AI matched the right line from every quote, fix any price it read wrongly, and set how much you need.
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 200 }}>Material</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Required qty</TableCell>
                    {quotes.map((q) => (
                      <TableCell key={q.id} sx={{ minWidth: 220 }}>
                        <Typography variant="subtitle2" noWrap title={q.supplier_name}>{q.supplier_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{q.currency} · unit price excl. tax</Typography>
                      </TableCell>
                    ))}
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {materials.map((m) => {
                    const cheapest = cheapestQuote(m, quotes, priceOf, rateOf);
                    return (
                      <TableRow key={m.id}>
                        <TableCell>
                          <TextField
                            size="small" fullWidth variant="standard" value={m.name}
                            onChange={(e) => updateMaterial(m.id, { name: e.target.value })}
                          />
                          <Typography variant="caption" color="text.secondary">{m.unit || ''}</Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small" type="number" value={m.required_qty} error={!(num(m.required_qty) > 0)}
                            onChange={(e) => updateMaterial(m.id, { required_qty: e.target.value })}
                            inputProps={{ min: 0, step: 'any' }} sx={{ width: 110 }}
                          />
                        </TableCell>
                        {quotes.map((q) => {
                          const lineNo = m.offers[q.id];
                          const isCheapest = cheapest.includes(q.id);
                          return (
                            <TableCell key={q.id} sx={{ verticalAlign: 'top', backgroundColor: isCheapest ? 'rgba(46,125,50,0.10)' : undefined }}>
                              <Select
                                size="small" fullWidth displayEmpty value={lineNo ?? ''}
                                onChange={(e) => setOffer(m.id, q.id, e.target.value)}
                                renderValue={(v) => (v === '' ? <em>Not quoted</em> : lineOf(q.id, v)?.description)}
                                sx={{ fontSize: 13, mb: 0.5 }}
                              >
                                <MenuItem value=""><em>Not quoted</em></MenuItem>
                                {q.items.map((i) => (
                                  <MenuItem key={i.line_no} value={i.line_no} sx={{ fontSize: 13 }}>
                                    {i.line_no}. {i.description} — {fmt(i.quantity, 0)} {i.unit || ''} @ {fmt(i.unit_price)}
                                  </MenuItem>
                                ))}
                              </Select>
                              {lineNo !== undefined && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <TextField
                                    size="small" type="number" value={prices[priceKey(m.id, q.id)] ?? ''}
                                    onChange={(e) => setPrice(m.id, q.id, e.target.value)}
                                    inputProps={{ min: 0, step: 'any' }} sx={{ width: 130 }}
                                    InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.5 }}>{q.currency}</Typography> }}
                                  />
                                  {isCheapest && <Chip size="small" color="success" label="Lowest" />}
                                </Box>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell>
                          <Tooltip title="Remove material">
                            <IconButton size="small" onClick={() => removeMaterial(m.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {unmatchedLines.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2">Quote lines not in the comparison</Typography>
                {unmatchedLines.map(({ quote, line }) => (
                  <Box key={`${quote.id}-${line.line_no}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      <b>{quote.supplier_name}</b> — {line.description} ({fmt(line.quantity, 0)} {line.unit || ''} @ {quote.currency} {fmt(line.unit_price)})
                    </Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={() => addMaterialFromLine(quote, line)}>Add as material</Button>
                  </Box>
                ))}
              </Paper>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
              {(missingRates.length > 0 || badQty) && (
                <Typography variant="body2" color="error">
                  {missingRates.length > 0 ? `Enter the exchange rate for ${missingRates.join(', ')}. ` : ''}
                  {badQty ? 'Every material needs a quantity above 0.' : ''}
                </Typography>
              )}
              <Button
                variant="contained" onClick={optimise}
                disabled={!!busy || materials.length === 0 || missingRates.length > 0 || badQty}
              >
                {busy === 'optimise' ? <CircularProgress size={20} color="inherit" /> : 'Find best combination'}
              </Button>
            </Box>
          </>
        )}

        {/* ------------------------------------------------ step 2: result */}
        {step === 2 && result && (
          <>
            {stale && (
              <Alert
                severity="info" sx={{ mb: 2 }}
                action={<Button color="inherit" size="small" onClick={optimise} disabled={!!busy || missingRates.length > 0 || badQty}>Re-run</Button>}
              >
                Prices, quantities or materials changed since the recommendation was made. Re-run to update it before creating POs.
              </Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
              <StatCard label="Your selection" value={`${baseCurrency} ${fmt(selectionTotal)}`} note={`${orders.length} PO${orders.length === 1 ? '' : 's'} · excl. tax`} />
              <StatCard label="Recommended (cheapest)" value={`${baseCurrency} ${fmt(result.optimised_total)}`} note={`${result.suppliers_used.length} supplier${result.suppliers_used.length === 1 ? '' : 's'}`} />
              <StatCard
                label="Best single supplier"
                value={result.best_single_supplier ? `${baseCurrency} ${fmt(result.best_single_supplier.total)}` : '—'}
                note={result.best_single_supplier ? quoteById[result.best_single_supplier.quote_id]?.supplier_name : 'No supplier quoted everything'}
              />
              <StatCard
                label="Saving vs single supplier"
                value={result.best_single_supplier ? `${baseCurrency} ${fmt(result.best_single_supplier.total - selectionTotal)}` : '—'}
                note="with your selection"
                positive={result.best_single_supplier && result.best_single_supplier.total - selectionTotal > 0}
              />
            </Box>

            {result.unquoted_material_ids.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No supplier quoted: {result.unquoted_material_ids.map((id) => materials.find((m) => m.id === id)?.name || id).join(', ')}. These are left out.
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Order split</Typography>
              <Button size="small" startIcon={<RestartAltIcon />} onClick={resetToRecommendation} disabled={anyPoCreated}>Reset to recommendation</Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Material</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Qty</TableCell>
                    <TableCell sx={{ minWidth: 260 }}>Supplier</TableCell>
                    <TableCell sx={{ width: 160 }}>Unit price (quote currency)</TableCell>
                    <TableCell align="right">Unit price ({baseCurrency})</TableCell>
                    <TableCell align="right">Line total ({baseCurrency})</TableCell>
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selection.map((s) => {
                    const rec = recommended[s.material.id];
                    const overridden = rec && rec.quote_id !== s.quoteId;
                    const locked = poResults[s.quoteId]?.success;
                    return (
                      <TableRow key={s.material.id}>
                        <TableCell>
                          {s.material.name}
                          <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5 }}>
                            {overridden
                              ? <Chip size="small" color="warning" label="Changed by you" />
                              : <Chip size="small" color="success" variant="outlined" label="Recommended" />}
                            {!overridden && rec?.tied_with.length > 0 && (
                              <Tooltip title={`Same price from ${rec.tied_with.map((q) => quoteById[q]?.supplier_name).join(', ')} — picked this supplier to keep the number of POs down.`}>
                                <Chip size="small" variant="outlined" label="Tie" />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small" type="number" value={s.material.required_qty} disabled={locked}
                            onChange={(e) => updateMaterial(s.material.id, { required_qty: e.target.value })}
                            inputProps={{ min: 0, step: 'any' }}
                            InputProps={{ endAdornment: <Typography variant="caption" sx={{ ml: 0.5 }}>{s.line?.unit || ''}</Typography> }}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small" fullWidth value={s.quoteId} disabled={locked}
                            onChange={(e) => setAssignment((a) => ({ ...a, [s.material.id]: e.target.value }))}
                          >
                            {Object.keys(s.material.offers).filter((q) => priceOf(s.material.id, q) !== null).map((q) => {
                              const base = priceOf(s.material.id, q) * (rateOf(quoteById[q]?.currency) || 0);
                              return (
                                <MenuItem key={q} value={q}>
                                  {quoteById[q]?.supplier_name} — {baseCurrency} {fmt(base)}{rec?.quote_id === q ? '  ★' : ''}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small" type="number" value={prices[priceKey(s.material.id, s.quoteId)] ?? ''} disabled={locked}
                            onChange={(e) => setPrice(s.material.id, s.quoteId, e.target.value)}
                            inputProps={{ min: 0, step: 'any' }}
                            InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.5 }}>{s.quote?.currency}</Typography> }}
                          />
                        </TableCell>
                        <TableCell align="right">{fmt(s.unitPriceBase)}</TableCell>
                        <TableCell align="right">{fmt(s.lineTotalBase)}</TableCell>
                        <TableCell>
                          <Tooltip title="Remove from order">
                            <span>
                              <IconButton size="small" disabled={locked} onClick={() => removeMaterial(s.material.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell colSpan={5} align="right"><b>Total (excl. tax)</b></TableCell>
                    <TableCell align="right"><b>{fmt(selectionTotal)}</b></TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Purchase orders to create</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              One PO per supplier, in that supplier's currency. SAP needs its own vendor and material codes — leave the vendor code blank to look the supplier up by name.
            </Typography>
            {orders.map(({ quote, lines }) => {
              const outcome = poResults[quote.id];
              const subtotal = lines.reduce((sum, s) => sum + (s.unitPrice || 0) * s.qty, 0);
              return (
                <Paper key={quote.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle2">{quote.supplier_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Quote {quote.quotation_number || '—'} · {quote.quotation_date || 'no date'} · {quote.currency} {fmt(subtotal)} excl. tax
                      </Typography>
                    </Box>
                    <TextField
                      size="small" label="SAP vendor code" value={supplierCodes[quote.id] || ''} disabled={outcome?.success}
                      onChange={(e) => setSupplierCodes((c) => ({ ...c, [quote.id]: e.target.value }))}
                      placeholder="Look up by name"
                    />
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Material</TableCell>
                        <TableCell sx={{ width: 190 }}>SAP material code</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Unit price ({quote.currency})</TableCell>
                        <TableCell>Tax code</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lines.map((s) => (
                        <TableRow key={s.material.id}>
                          <TableCell>{s.material.name}</TableCell>
                          <TableCell>
                            <TextField
                              size="small" value={sapMaterial[s.material.id] || ''} disabled={outcome?.success}
                              error={!sapMaterial[s.material.id]} placeholder="Required"
                              onChange={(e) => setSapMaterial((m) => ({ ...m, [s.material.id]: e.target.value.trim() }))}
                            />
                          </TableCell>
                          <TableCell align="right">{fmt(s.qty, 0)} {s.line?.unit || ''}</TableCell>
                          <TableCell align="right">{fmt(s.unitPrice)}</TableCell>
                          <TableCell>{s.line?.tax_code || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {outcome && (
                    <Alert severity={outcome.success ? 'success' : 'error'} icon={outcome.success ? <CheckCircleIcon /> : undefined} sx={{ mt: 1 }}>
                      {outcome.message}
                    </Alert>
                  )}
                </Paper>
              );
            })}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center', mb: 4 }}>
              {!canOrder && selection.length > 0 && (
                <Typography variant="body2" color="error">
                  {stale ? 'Re-run the recommendation first.' : 'Every line needs a price and a quantity above 0.'}
                </Typography>
              )}
              <Button
                variant="contained" color="primary"
                disabled={!canOrder || pendingOrders.length === 0 || !!busy}
                onClick={() => setConfirmOpen(true)}
              >
                {busy === 'po'
                  ? <><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />Creating POs…</>
                  : pendingOrders.length === 0 ? 'All POs created'
                    : `Create ${pendingOrders.length} PO${pendingOrders.length === 1 ? '' : 's'} in SAP`}
              </Button>
            </Box>
          </>
        )}
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create purchase orders in SAP?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>This posts the following to SAP. It cannot be undone from here.</Typography>
          {pendingOrders.map(({ quote, lines }) => (
            <Typography key={quote.id} variant="body2">
              • <b>{quote.supplier_name}</b>: {lines.length} line{lines.length === 1 ? '' : 's'}, {quote.currency} {fmt(lines.reduce((sum, s) => sum + (s.unitPrice || 0) * s.qty, 0))} excl. tax
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createPOs}>Create POs</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Quotes whose converted price is the lowest for this material (for highlighting).
function cheapestQuote(material, quotes, priceOf, rateOf) {
  const converted = quotes
    .filter((q) => material.offers[q.id] !== undefined)
    .map((q) => {
      const price = priceOf(material.id, q.id);
      const rate = rateOf(q.currency);
      return { id: q.id, value: price !== null && rate ? price * rate : null };
    })
    .filter((c) => c.value !== null);
  if (converted.length < 2) return [];
  const min = Math.min(...converted.map((c) => c.value));
  return converted.filter((c) => Math.abs(c.value - min) < 1e-6).map((c) => c.id);
}

const QuoteSummary = ({ quotes }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${Math.min(quotes.length, 5)}, 1fr)` }, gap: 1.5, mb: 2 }}>
    {quotes.map((q) => (
      <Paper key={q.id} variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" noWrap title={q.supplier_name}>{q.supplier_name}</Typography>
        <Typography variant="caption" color="text.secondary" component="div" noWrap title={q.file_name}>{q.file_name}</Typography>
        <Typography variant="caption" component="div">
          Quote {q.quotation_number || '—'} · {q.quotation_date || 'no date'} · {q.currency || '?'} · {q.items.length} lines
        </Typography>
        {q.payment_terms && <Typography variant="caption" color="text.secondary" component="div" noWrap title={q.payment_terms}>{q.payment_terms}</Typography>}
      </Paper>
    ))}
  </Box>
);

const StatCard = ({ label, value, note, positive }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" sx={{ fontWeight: 700, color: positive ? 'success.main' : undefined }}>{value}</Typography>
    {note && <Typography variant="caption" color="text.secondary" noWrap component="div" title={note}>{note}</Typography>}
  </Paper>
);

export default PurchaseOptimizer;
